<template>
  <v-card flat>
    <vx-confirm-dialog ref="noMod5Confirmation"/>
    <v-card-title class="text-h5">Data Preparation</v-card-title>
    <v-card-subtitle>
      In this module the user must select the land use – land use change datasets sources to be applied in the analysis.<br/>
      Default global datasets are able to be used, however, the user is able to upload its own datasets.<br/>
      Periods and activities to be included in the analysis must be indicated.
    </v-card-subtitle>

    <v-card-text>
      <v-row>
        <!-- Input form -->
        <v-col>
          <v-card elevation="4" :disabled="finishedProcessing">
            <v-card-text>
              <v-radio-group v-model="sourceSelection" column>
                <!-- Global sources -->
                <v-radio :value="0" label="Use global sources"/>
                <v-expand-transition>
                  <v-form ref="formGlobalSources" v-model="isValidGlobalSources" v-show="sourceSelection == 0">
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="globalSource" :items="globalSourcesList" label="Global Source" prepend-icon="mdi-semantic-web" outlined dense :rules="[globalRules.required]"/>
                      </v-col>
                      <v-col sm="2">
                        <v-text-field :disabled="globalSource !== 'Global Forest Watch'" class="right-input" v-model="forestPercentage" label="Forest definition (%)" outlined dense suffix="%" type="number" :rules="[forestPercentageValid]"/> <!-- minimum tree cover -->
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col sm="6">
                        <v-card elevation="4">
                          <v-card-title><small>Testing period</small></v-card-title>
                          <v-card-text>
                            <v-row justify="center">
                              <v-col sm="4">
                                <v-select v-model="testingPeriod[0]" :items="yearList" label="Init year*" outlined dense :rules="[globalRules.required, isTestingPeriodValid]">
                                  <!-- <template v-slot:selection="{ item }">
                                    <span class="right-input">{{ item }}</span>
                                  </template> -->
                                </v-select>
                              </v-col>

                              <v-col sm="4">
                                <v-select v-model="testingPeriod[1]" :items="yearList.filter(x => x > testingPeriod[0])" label="End year*" outlined dense :rules="[globalRules.required, isTestingPeriodValid]"/>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>

                      <v-col sm="6">
                        <v-card elevation="4">
                          <v-card-title><small>Validation period</small></v-card-title>
                          <v-card-text>
                            <v-row justify="center">
                              <v-col sm="4">
                                <v-text-field :value="validationInit" label="Init year*" readonly outlined dense :rules="[globalRules.required]" hint="Read only slot"/>
                              </v-col>

                              <v-col sm="4">
                                <v-text-field :value="validationEnd" label="End year*" readonly outlined dense :rules="[globalRules.required, isValidationPeriodValid]" hint="Read only slot"/>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-expand-transition>

                <v-divider class="my-6"/>

                <!-- Forest -->
                <v-radio :value="1" label="Forest"/>
                <v-expand-transition>
                  <v-form ref="formForest" v-model="isValidForest" v-show="sourceSelection == 1">
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="forestLayer1" :items="forestLayers" item-text="NOMBRE" return-object label="Forest year 1" prepend-icon="mdi-file" outlined dense :rules="[globalRules.required]"/>
                      </v-col>
                      <v-col sm="2">
                        <v-select v-model="forestLayer1Year" :items="yearList" label="Year*" outlined dense :rules="[globalRules.required, isForestYearsValid]"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="forestLayer2" :items="forestLayers" item-text="NOMBRE" return-object label="Forest year 2" prepend-icon="mdi-file" outlined dense :rules="[globalRules.required]"/>
                      </v-col>
                      <v-col sm="2">
                        <v-select v-model="forestLayer2Year" :items="yearList.filter(x => x > forestLayer1Year)" label="Year*" outlined dense :rules="[globalRules.required, isForestYearsValid]"/>
                      </v-col>
                      </v-row>
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="forestLayer3" :items="forestLayers" item-text="NOMBRE" return-object label="Forest year 3" prepend-icon="mdi-file" outlined dense :rules="[globalRules.required]"/>
                      </v-col>
                      <v-col sm="2">
                        <v-select v-model="forestLayer3Year" :items="yearList.filter(x => x > forestLayer2Year)" label="Year*" outlined dense :rules="[globalRules.required, isForestYearsValid]"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="forestVat" :items="vatFiles" item-text="NOMBRE" return-object label="VAT" prepend-icon="mdi-file" outlined dense :rules="[globalRules.required]"/>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-expand-transition>

                <v-divider class="my-6"/>

                <!-- Use change -->
                <v-radio :value="2" label="Use change"/>
                <v-expand-transition>
                  <v-form ref="formUseChange" v-model="isValidUseChange" v-show="sourceSelection == 2">
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="useChangeLayer1" :items="useChangeLayers" item-text="NOMBRE" return-object label="Use change - Period 1" prepend-icon="mdi-file"
                          outlined dense :rules="[globalRules.required]"/>
                      </v-col>

                      <v-col sm="2">
                        <v-select v-model="useChangeLayer1Period[0]" :items="yearList" label="Period init*"
                          outlined dense :rules="[globalRules.required, isUseChange1PeriodValid]"/>
                      </v-col>

                      <v-col sm="2">
                        <v-select v-model="useChangeLayer1Period[1]" :items="yearList.filter(x => x > useChangeLayer1Period[0])" label="Period end*"
                          outlined dense :rules="[globalRules.required, isUseChangePeriodJumpValid, isUseChange1PeriodValid]"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="useChangeLayer2" :items="useChangeLayers" item-text="NOMBRE" return-object label="Use change - Period 2" prepend-icon="mdi-file"
                          outlined dense :rules="[globalRules.required]"/>
                      </v-col>

                      <v-col sm="2">
                        <v-select v-model="useChangeLayer2Period[0]" :items="yearList.filter(x => x > useChangeLayer1Period[1])" label="Period init*"
                          outlined dense :rules="[globalRules.required, isUseChangePeriodJumpValid, isUseChange2PeriodValid]"/>
                      </v-col>

                      <v-col sm="2">
                        <v-select v-model="useChangeLayer2Period[1]" :items="yearList.filter(x => x > useChangeLayer2Period[0])" label="Period end*"
                          outlined dense :rules="[globalRules.required, isUseChange2PeriodValid]"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col sm="6">
                        <v-select v-model="useChangeVat" :items="vatFiles" item-text="NOMBRE" return-object label="VAT" prepend-icon="mdi-file" outlined dense :rules="[globalRules.required]"/>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-expand-transition>

                <v-divider class="my-6"/>

                <!-- Planned / Unplanned -->
                <v-row>
                  <v-col sm="8">
                    <v-card elevation="4">
                      <v-card-title><small>Deforestation activities analyzed</small></v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col>
                            <v-checkbox class="mt-0" v-model="plannedDeforestation" label="Planned Deforestation"/> <!-- Modulo 1 y 4 -->
                          </v-col>
                          <v-col>
                            <v-checkbox class="mt-0" v-model="unplannedDeforestation" label="Unplanned Deforestation"/> <!-- Solo 2,3,4 -->
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-radio-group>

              <v-btn width="150" color="primary" :disabled="!isPremoduleValid" :loading="isInitProcessing" @click="validateYearsToM5AndProcess">Process</v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Results -->
        <v-col>
          <v-card elevation="4">
            <v-card-title>Results:</v-card-title>

            <v-card-text>
              <vx-result-file-list :fileList="resultFiles"/>
            </v-card-text>

            <v-card-actions>
              <!-- <v-btn v-show="resultFiles.length > 0" color="primary" outlined>
                <v-icon left>mdi-eye</v-icon>
                View on map
              </v-btn> -->
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import api from '../../api'
import ResultFileList from './ResultFileList.vue'
import ConfirmDialog from '../shared/ConfirmDialog'

const LATEST_DATA_YEAR = 2025

export default {
  name: 'Premodule',

  props: {
    region: Object,
    currentProcess: String
  },

  components: {
    'vx-result-file-list': ResultFileList,
    'vx-confirm-dialog': ConfirmDialog
  },

  data: () => ({
    isValidGlobalSources: false,
    isValidForest: false,
    isValidUseChange: false,

    // Periodic checking
    isInitProcessing: false,
    isProcessing: false,
    intervalCall: null,
    finishedProcessing: false,

    // Input
    sourceSelection: 0,

    globalSourcesList: ['Global Forest Watch', 'Forobs'],
    globalSource: null,
    forestPercentage: 25,
    testingPeriod: [null, null],

    forestLayer1: null,
    forestLayer1Year: null,
    forestLayer2: null,
    forestLayer2Year: null,
    forestLayer3: null,
    forestLayer3Year: null,
    forestVat: null,

    useChangeLayer1: null,
    useChangeLayer1Period: [null, null],
    useChangeLayer2: null,
    useChangeLayer2Period: [null, null],
    useChangeVat: null,

    plannedDeforestation: false,
    unplannedDeforestation: false,

    // Output
    resultFiles: [],

    // Module5 max year
    m5Forobs: LATEST_DATA_YEAR,
    m5GFW: LATEST_DATA_YEAR,
    isAvailableMod5: true
  }),

  computed: {
    vatFiles () {
      return this.$store.getters['file/filesByRegionAndType'](this.region, 11) // 11 == VAT File
    },
    yearList () {
      let initYear = 1990
      if (this.globalSource === this.globalSourcesList[0]) {
        initYear = 2000
      }
      let list = []
      for (let i = LATEST_DATA_YEAR; i >= initYear; i--) {
        list.push(i)
      }

      return list
    },
    maxYear () {
      return LATEST_DATA_YEAR
    },
    forestPercentageValid () {
      return (this.globalSource === 'Forobs' || (this.forestPercentage > 0 && this.forestPercentage < 100)) || 'Must be between 0 and 100'
    },
    isTestingPeriodValid () {
      return (this.testingPeriod[0] < this.testingPeriod[1]) || 'Init year must be prior to end year'
    },
    isValidationPeriodValid () {
      return (this.validationEnd <= this.maxYear) || `This value should be lower than ${this.maxYear}. Modify Testing Period`
    },
    validationInit () {
      let value = null
      if (this.testingPeriod[1]) {
        value = this.testingPeriod[1] + 1
      }
      return value
    },
    validationEnd () {
      let value = null
      if (this.validationInit) {
        let diff = this.testingPeriod[1] - this.testingPeriod[0]
        value = this.validationInit + diff
      }
      return value
    },
    isForestYearsValid () {
      return (!!this.forestLayer1Year && !!this.forestLayer2Year && !!this.forestLayer3Year) ||
      ((this.forestLayer1Year < this.forestLayer2Year) && (this.forestLayer2Year < this.forestLayer3Year)) || 'Years must be in ascending order'
    },
    isUseChange1PeriodValid () {
      return (this.useChangeLayer1Period[0] < this.useChangeLayer1Period[1]) || 'Init year must be prior to end year'
    },
    isUseChangePeriodJumpValid () {
      return (this.useChangeLayer1Period[1] < this.useChangeLayer2Period[0]) || 'Period 1 must be prior to period 2'
    },
    isUseChange2PeriodValid () {
      return (this.useChangeLayer2Period[0] < this.useChangeLayer2Period[1]) || 'Init year must be prior to end year'
    },
    isPremoduleValid () {
      return ((this.isValidGlobalSources || this.isValidForest || this.isValidUseChange) && this.isTestingPeriodValid && (this.plannedDeforestation || this.unplannedDeforestation))
    },
    selectedSource () {
      if (this.sourceSelection === 0) {
        // Global Sources: gfw / forobs
        return this.globalSource === 'Global Forest Watch' ? 'gfw' : 'forobs'
      } else if (this.sourceSelection === 1) {
        return 'forest'
      } else if (this.sourceSelection === 2) {
        return 'useChange'
      }
      return ''
    },
    forestLayers () {
      return this.$store.getters['file/filesByRegionAndType'](this.region, 4) // 4 == Forest map
    },
    useChangeLayers () {
      return this.$store.getters['file/filesByRegionAndType'](this.region, 5) // 5 == Use change map
    }
  },

  watch: {
    sourceSelection () {
      if (this.sourceSelection === 0) {
        this.$refs.formForest.reset()
        this.$refs.formUseChange.reset()
      } else if (this.sourceSelection === 1) {
        this.$refs.formGlobalSources.reset()
        this.$refs.formUseChange.reset()
      } else if (this.sourceSelection === 2) {
        this.$refs.formGlobalSources.reset()
        this.$refs.formForest.reset()
      }
    },

    isProcessing (val) {
      this.isInitProcessing = val

      if (val && !this.intervalCall) {
        this.intervalCall = setInterval(() => {
          api.process.getResults(this.currentProcess, 0).then(response => {
            if (response.data.error) {
              this.stopProcessing()

              console.error(response.data.error)
              this.$toastr.e(response.data.message)
            } else {
              if (response.data.finished) {
                this.resultFiles = response.data.results.files

                this.$toastr.s('Premodule succesfully processed')

                this.finishedProcessing = true
                this.$emit('onProcessed', {
                  plannedDeforestation: this.plannedDeforestation,
                  unplannedDeforestation: this.unplannedDeforestation,
                  isAvailableMod5: this.isAvailableMod5
                })

                this.stopProcessing()
                this.setReportFile(response.data.results.report)
              } else {
                // Not finished
              }
            }
          }).catch(error => {
            this.stopProcessing()

            console.error(error)
            this.$toastr.e('Error processing premodule')
          }).finally(() => {
            // Nothing
          })
        }, 15 * 1000) // TODO: ajustar tiempo
      } else {
        clearInterval(this.intervalCall)
        this.intervalCall = null
      }
    }
  },

  mounted () {
    this.$root.$noMod5Confirmation = this.$refs.noMod5Confirmation // Asociar referencia global al ConfirmDialog
  },

  methods: {
    ...mapActions('app', [
      'setIsProcessing'
    ]),
    ...mapActions('currentProcess', [
      'setReportFile'
    ]),

    stopProcessing () {
      this.isProcessing = false
      clearInterval(this.intervalCall)
      this.intervalCall = null

      this.setIsProcessing(false)
    },

    reset () {
      this.finishedProcessing = false

      this.isValidGlobalSources = false
      this.isValidForest = false
      this.isValidUseChange = false

      // Input
      this.sourceSelection = 0

      this.$refs.formGlobalSources.reset()
      this.globalSource = null
      this.forestPercentage = 25
      this.testingPeriod = [null, null]

      this.forestLayer1 = null
      this.forestLayer1Year = null
      this.forestLayer2 = null
      this.forestLayer2Year = null
      this.forestLayer3 = null
      this.forestLayer3Year = null
      this.forestVat = null

      this.useChangeLayer1 = null
      this.useChangeLayer1Period = [null, null]
      this.useChangeLayer2 = null
      this.useChangeLayer2Period = [null, null]
      this.useChangeVat = null

      this.plannedDeforestation = false
      this.unplannedDeforestation = false

      // Output
      this.resultFiles = []

      this.isAvailableMod5 = true
    },

    async process () {
      let data = {
        processId: this.currentProcess,
        selectedSource: this.selectedSource,
        forestFiles: [this.forestLayer1, this.forestLayer2, this.forestLayer3],
        forestYears: [this.forestLayer1Year, this.forestLayer2Year, this.forestLayer3Year],
        forestVat: this.forestVat,
        useChangeFiles: [this.useChangeLayer1, this.useChangeLayer2],
        useChangeVat: this.useChangeVat,
        testingPeriod: this.testingPeriod,
        validationPeriod: [this.validationInit, this.validationEnd],
        useChangeLayer1Period: this.useChangeLayer1Period,
        useChangeLayer2Period: this.useChangeLayer2Period,
        forestCoverPercentage: this.forestPercentage,
        plannedDefor: this.plannedDeforestation,
        unplannedDefor: this.unplannedDeforestation
      }

      try {
        this.setIsProcessing(true)
        this.isInitProcessing = true

        await api.process.premodule(data)

        this.isProcessing = true
        this.$toastr.s('Premodule starts processing...')
      } catch (error) {
        this.isInitProcessing = false

        console.error(error)
        this.$toastr.e('Error processing premodule')
      }
    },

    askProceedWithoutM5 () {
      var self = this
      this.$root.$noMod5Confirmation.open('Conflict with Input Years', 'Selected Years doesn\'t allow to make Module 5 calculations. Are you sure you want to continue with the current values?').then(result => {
        if (result) {
          self.process()
          self.isAvailableMod5 = false
        }
      })
    },

    validateYearsToM5AndProcess () {
      let diffYears = this.validationEnd - this.validationInit
      let year3 = (this.validationEnd + 1) + diffYears
      let stadisticsBegin = this.validationEnd + 1

      let referenceYear = null
      if ((this.globalSource === 'Global Forest Watch')) {
        referenceYear = this.m5GFW
      } else {
        referenceYear = this.m5Forobs
      }

      // console.log('InBegin', stadisticsBegin)
      // console.log('InYear3', year3)
      // console.log('Reference', referenceYear)
      // console.log('referenceYear > stadisticsBegin')
      // console.log(referenceYear > stadisticsBegin)
      // console.log('referenceYear < year3')
      // console.log(referenceYear < year3)
      if (referenceYear > stadisticsBegin && referenceYear < year3) {
        while (referenceYear < year3) {
          year3 -= 1
        }
      }
      // console.log('OutBegin', stadisticsBegin)
      // console.log('OutYear3', year3)
      // console.log('referenceYear < stadisticsBegin')
      // console.log(referenceYear < stadisticsBegin)
      // console.log('year3 < stadisticsBegin + 1')
      // console.log(year3 < stadisticsBegin + 1)
      if (referenceYear <= stadisticsBegin) {
        this.askProceedWithoutM5()
      } else if (year3 < stadisticsBegin + 1) {
        this.askProceedWithoutM5()
      } else {
        this.process()
      }

      // if ((this.globalSource === 'Global Forest Watch' && year3 > this.m5GFW) || (this.globalSource === 'Forobs' && year3 > this.m5Forobs)) {
      //   this.askProceedWithoutM5()
      // } else {
      //   this.process()
      // }
    }
  }
}
</script>

<style scoped>
  .right-input >>> input {
          text-align: right
  }
</style>
